/**
 * The teacher's shared standards default, held by Objectives, against OSA's own profile row.
 * Pure.
 *
 * Objectives stores one partial default per teacher and every Labs tool merges its picks
 * into it. OSA keeps its own `profiles` row as well, and on read the two are compared as
 * whole rows by when they were written: the newer one is what the ask form opens with. A
 * field-by-field merge would show a combination the teacher never picked.
 */
import { ACADEMIC_SUBJECTS, GRADE_LEVELS, JURISDICTIONS, type AcademicSubject, type GradeLevel, type Jurisdiction, type Profile, isPlainObject } from './profile';

/** The three fields as Objectives carries them: any may be missing. */
export interface StandardsFields {
  jurisdiction?: string;
  academicSubject?: string;
  gradeLevels?: string[];
}

/** What `GET /api/standards-default` answers with, when there is a default. */
export interface SharedDefault extends StandardsFields {
  updatedAt: string;
  updatedByTool: string | null;
}

/**
 * What reading the shared default came back with. `none` is only Objectives answering that
 * there is no default; a read that failed for any reason is `unavailable`, which says nothing
 * about what Objectives holds, so it must never be taken as permission to seed.
 */
export type SharedRead =
  | { kind: 'found'; default: SharedDefault }
  | { kind: 'none' }
  | { kind: 'unavailable' };

/** A PATCH body. `matchedPrefill` says the teacher submitted the prefilled values unchanged. */
export interface StandardsPick extends StandardsFields {
  matchedPrefill?: boolean;
}

/** OSA's own row with the time it was last saved. */
export interface OwnProfileRow {
  profile: Profile;
  updatedAt: string;
}

/** Null, `''` and `[]` count as not sent, as they do in Objectives' PATCH. */
export function nonEmptyFields(fields: StandardsFields): StandardsFields {
  return {
    ...(typeof fields.jurisdiction === 'string' && fields.jurisdiction !== '' ? { jurisdiction: fields.jurisdiction } : {}),
    ...(typeof fields.academicSubject === 'string' && fields.academicSubject !== '' ? { academicSubject: fields.academicSubject } : {}),
    ...(Array.isArray(fields.gradeLevels) && fields.gradeLevels.length > 0 ? { gradeLevels: [...fields.gradeLevels] } : {}),
  };
}

const isEmpty = (fields: StandardsFields) => Object.keys(nonEmptyFields(fields)).length === 0;

/**
 * The shared default as an OSA profile, or null when it has no state OSA offers.
 *
 * A value OSA does not offer is dropped rather than refused, so a subject another tool
 * allows still leaves the state and grades in place. The state is required because OSA's
 * profile is the Learning Commons query and cannot run without one.
 */
export function profileFromShared(shared: StandardsFields): Profile | null {
  const jurisdiction = JURISDICTIONS.find((j) => j === shared.jurisdiction);
  if (!jurisdiction) return null;
  const subject = ACADEMIC_SUBJECTS.find((s) => s === shared.academicSubject);
  const grades = shared.gradeLevels ?? [];
  return {
    jurisdiction: jurisdiction as Jurisdiction,
    ...(subject ? { academicSubject: subject as AcademicSubject } : {}),
    gradeLevels: GRADE_LEVELS.filter((g) => grades.includes(g)) as GradeLevel[],
  };
}

/** Whether `a` was written after `b`. ISO timestamps; an unparseable one falls back to text order. */
function newer(a: string, b: string): boolean {
  const left = Date.parse(a);
  const right = Date.parse(b);
  return Number.isNaN(left) || Number.isNaN(right) ? a > b : left > right;
}

export interface ProfileRead {
  /** What `GET /api/profile` answers with. */
  profile: Profile | null;
  /** The fields to PATCH into the shared default, or null to leave it alone. */
  seed: StandardsFields | null;
}

/**
 * Which profile the ask form opens with, and whether OSA's own row should seed the default.
 *
 * The newer whole row wins; a tie goes to the shared default, because the PATCH that follows
 * every OSA save stamps the default after the row. The own row seeds when it is newer or the
 * default is empty, and only its non-empty fields are sent, so a row with no subject never
 * erases the subject another tool stored. When the default could not be read, the own row is
 * shown but never seeds: the default it would overwrite may be newer.
 */
export function decideProfileRead(input: { own: OwnProfileRow | null; shared: SharedRead }): ProfileRead {
  const own = input.own;
  if (input.shared.kind === 'unavailable') return { profile: own?.profile ?? null, seed: null };
  const shared = input.shared.kind === 'found' && !isEmpty(input.shared.default) ? input.shared.default : null;
  if (!own) return { profile: shared ? profileFromShared(shared) : null, seed: null };
  if (!shared || newer(own.updatedAt, shared.updatedAt)) return { profile: own.profile, seed: nonEmptyFields(own.profile) };
  // A newer default with no state OSA offers cannot become an OSA profile, so the own row stays.
  return { profile: profileFromShared(shared) ?? own.profile, seed: null };
}

/**
 * The body of Objectives' GET and PATCH answers, read as untrusted input. Anything that is
 * not `{ default: { ..., updatedAt } }` is no default; fields of the wrong type are dropped.
 */
export function parseSharedDefault(body: unknown): SharedDefault | null {
  if (!isPlainObject(body) || !isPlainObject(body.default)) return null;
  const raw = body.default;
  if (typeof raw.updatedAt !== 'string') return null;
  return {
    ...(typeof raw.jurisdiction === 'string' ? { jurisdiction: raw.jurisdiction } : {}),
    ...(typeof raw.academicSubject === 'string' ? { academicSubject: raw.academicSubject } : {}),
    ...(Array.isArray(raw.gradeLevels) && raw.gradeLevels.every((g) => typeof g === 'string') ? { gradeLevels: raw.gradeLevels as string[] } : {}),
    updatedAt: raw.updatedAt,
    updatedByTool: typeof raw.updatedByTool === 'string' ? raw.updatedByTool : null,
  };
}

/**
 * A 200 body from Objectives' GET as a `SharedRead`: `{ default: null }` is `none`, a valid
 * default is `found`, and anything else is `unavailable` rather than a guess at "no default".
 */
export function parseSharedRead(body: unknown): SharedRead {
  if (isPlainObject(body) && body.default === null) return { kind: 'none' };
  const found = parseSharedDefault(body);
  return found ? { kind: 'found', default: found } : { kind: 'unavailable' };
}

/** Same state, subject and grade set. Answers `matchedPrefill` on the frontend. */
export function sameProfile(a: Profile | null, b: Profile | null): boolean {
  if (!a || !b) return false;
  const grades = (p: Profile) => [...new Set(p.gradeLevels)].sort().join(',');
  return a.jurisdiction === b.jurisdiction && (a.academicSubject ?? '') === (b.academicSubject ?? '') && grades(a) === grades(b);
}
